import React, { useState, useEffect, useCallback } from "react";

const FALLBACK_GALLERIES = {
  vinduespudsning: {
    title: "Vinduespudsning Galleri",
    description: "Se udvalgte billeder af vores arbejde med vinduespolering og rengøring af ruder på Vestsjælland.",
    images: [
      "https://assets.auxodev.dk/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/img-1152_3fc2ce02.jpg",
      "https://assets.auxodev.dk/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/img-1811_35773c8c.jpg",
      "https://assets.auxodev.dk/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/fullsizerender_653b0c90.jpeg",
      "https://assets.auxodev.dk/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/img-2017_1da89854.jpg"
    ]
  },
  fliserens: {
    title: "Fliserens Galleri",
    description: "Professionel fliserens, algebehandling og imprægnering af terrasser og indkørsler.",
    images: [
      "https://assets.auxodev.dk/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/fliserens-have-terrasse-ren.png",
      "https://assets.auxodev.dk/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/fliserens-overfladerenser-udstyr.png",
      "https://assets.auxodev.dk/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/fliserens-ren-terrasse-have.png",
      "https://assets.auxodev.dk/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/galleri-fliserens-indkoersel.png"
    ]
  }
};

export default function CmsGallery({ slug = "vinduespudsning", title, subtitle }) {
  const [gallery, setGallery] = useState(FALLBACK_GALLERIES[slug] || FALLBACK_GALLERIES.vinduespudsning);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchGallery = async () => {
      try {
        setLoading(true);
        const res = await fetch(`https://api.dev.auxo.dk/public/v2/content/galleri?gallery_slug=${encodeURIComponent(slug)}`);
        if (res.ok) {
          const data = await res.json();
          if (data && data.items && data.items.length > 0) {
            const found = data.items.find((item) => item.gallery_slug === slug) || data.items[0];
            if (isMounted && found) {
              setGallery({
                title: found.title,
                description: found.description,
                images: Array.isArray(found.images) ? found.images : [found.images]
              });
            }
          }
        }
      } catch (err) {
        // Fallback already pre-set
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchGallery();
    return () => {
      isMounted = false;
    };
  }, [slug]);

  const images = gallery.images || [];

  const handlePrev = useCallback(() => {
    setLightboxIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const handleNext = useCallback(() => {
    setLightboxIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxIndex, handleNext, handlePrev]);

  if (images.length === 0) return null;

  return (
    <section className="py-16 sm:py-20 bg-white border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
            {title || gallery.title || "Vores arbejde i billeder"}
          </h2>
          <p className="mt-2 text-slate-600 text-sm sm:text-base">
            {subtitle || gallery.description || "Gennemse et udvalg af vores udførte opgaver for glade kunder."}
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {images.map((imgUrl, idx) => (
            <div
              key={idx}
              onClick={() => setLightboxIndex(idx)}
              className="group relative rounded-none overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer bg-slate-200 aspect-[4/3]"
            >
              <img
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${imgUrl.replace(/^https?:\/\/assets\.auxodev\.dk\//, '')}`}
                alt={`${gallery.title || 'Galleri billede'} ${idx + 1}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-slate-950/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm text-cyan-700 flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
                  <i className="fa-solid fa-magnifying-glass-plus text-lg" aria-hidden="true" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-8 animate-in fade-in duration-200"
          onClick={() => setLightboxIndex(null)}
        >
          <div
            className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={() => setLightboxIndex(null)}
              className="absolute -top-12 right-0 text-white/80 hover:text-white p-2 text-2xl focus:outline-none"
              aria-label="Luk visning"
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>

            {/* Large Image */}
            <div className="relative overflow-hidden rounded-none shadow-2xl bg-black max-h-[80vh] flex items-center justify-center">
              <img
                src={images[lightboxIndex]}
                alt={`Forstørret billede ${lightboxIndex + 1}`}
                className="max-h-[80vh] w-auto max-w-full object-contain"
              />
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between w-full mt-4 text-white text-sm">
              <button
                type="button"
                onClick={handlePrev}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-colors flex items-center gap-2"
              >
                <i className="fa-solid fa-chevron-left" aria-hidden="true" />
                <span>Forrige</span>
              </button>

              <span className="text-slate-300 font-medium">
                {lightboxIndex + 1} / {images.length}
              </span>

              <button
                type="button"
                onClick={handleNext}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-colors flex items-center gap-2"
              >
                <span>Næste</span>
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
